import { useMutation, useQuery } from "@tanstack/react-query";
import { CalendarOff, Camera, Check, ChevronRight, ClipboardPaste, ImagePlus, Plus, RotateCcw, Trash2, WandSparkles, X } from "lucide-react";
import { ChangeEvent, FormEvent, useMemo, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { DateInput } from "../components/DateInput";
import { PageHeader } from "../components/PageHeader";
import { ErrorScreen, LoadingScreen } from "../components/ScreenState";
import { api } from "../lib/api";
import { addDays, localDateKey } from "../lib/date";
import { prepareOcrImage } from "../lib/image";
import { queryClient } from "../lib/query";
import { getPreferredChildId, savePreferredChildId } from "../lib/preferred-child";
import type { HomeworkOcrResult } from "../lib/types";

type FormState = {
  subject: string;
  title: string;
  total: string;
  unit: string;
  targetDate: string;
};

type OcrDraft = FormState & {
  key: string;
  section: string;
};

type ExcludedDateRange = {
  id: string;
  start: string;
  end: string;
};

const SUBJECTS = ["语文", "数学", "英语", "科学", "美术", "书法", "劳动", "体育", "综合", "其他"];
const UNITS = ["项", "页", "题", "个", "篇", "分钟", "张", "首", "份", "本", "小时", "次"];

const initialForm = (): FormState => ({
  subject: "语文",
  title: "",
  total: "",
  unit: "页",
  targetDate: addDays(localDateKey(), 7),
});

function PlanningRules(props: {
  startDate: string;
  excludedWeekdays: number[];
  excludedRanges: ExcludedDateRange[];
  rangeStart: string;
  rangeEnd: string;
  onToggleWeekday: (day: number) => void;
  onRangeStart: (date: string) => void;
  onRangeEnd: (date: string) => void;
  onAddRange: () => void;
  onRemoveRange: (id: string) => void;
}) {
  return (
    <section className="planning-rules">
      <header><CalendarOff size={19} /><div><strong>计划日期设置</strong><small>按总量和截止日期均分，并避开勾选日期</small></div></header>
      <div className="weekday-options">
        <label><input type="checkbox" checked={props.excludedWeekdays.includes(6)} onChange={() => props.onToggleWeekday(6)} /> 周六不安排</label>
        <label><input type="checkbox" checked={props.excludedWeekdays.includes(0)} onChange={() => props.onToggleWeekday(0)} /> 周日不安排</label>
      </div>
      <div className="special-date-range">
        <strong>出游/其他事项日期范围</strong>
        <small>连续多天只需设置一次，开始和结束日期都不安排作业</small>
        <div className="special-date-range-grid">
          <label className="field"><span>开始日期</span><DateInput min={props.startDate} value={props.rangeStart} onChange={(event) => props.onRangeStart(event.target.value)} /></label>
          <label className="field"><span>结束日期</span><DateInput min={props.rangeStart || props.startDate} value={props.rangeEnd} onChange={(event) => props.onRangeEnd(event.target.value)} /></label>
        </div>
        <button className="small-button" type="button" disabled={!props.rangeStart || !props.rangeEnd} onClick={props.onAddRange}><Plus size={17} /> 添加日期范围</button>
      </div>
      {props.excludedRanges.length ? <div className="date-chips">{props.excludedRanges.map((range) => <button type="button" key={range.id} onClick={() => props.onRemoveRange(range.id)}>{range.start === range.end ? range.start : `${range.start} 至 ${range.end}`}<X size={14} /></button>)}</div> : null}
      <p>到计划日仍未完成的任务，会在下次打开应用时自动重排到截止日前的剩余可用日期。</p>
    </section>
  );
}

export function CapturePage() {
  const data = useQuery({ queryKey: ["bootstrap"], queryFn: api.bootstrap });
  const [form, setForm] = useState<FormState>(initialForm);
  const [preferredChildId, setPreferredChildId] = useState(getPreferredChildId);
  const [mode, setMode] = useState<"entry" | "success">("entry");
  const [createdTitle, setCreatedTitle] = useState("");
  const [createdCount, setCreatedCount] = useState(1);
  const [createdTargetDate, setCreatedTargetDate] = useState("");
  const [error, setError] = useState("");
  const [ocrImage, setOcrImage] = useState<{ dataUrl: string; bytes: number; width: number; height: number } | null>(null);
  const [ocrResult, setOcrResult] = useState<HomeworkOcrResult | null>(null);
  const [ocrDrafts, setOcrDrafts] = useState<OcrDraft[]>([]);
  const [bulkTargetDate, setBulkTargetDate] = useState(() => initialForm().targetDate);
  const [bulkDateApplied, setBulkDateApplied] = useState("");
  const [excludedWeekdays, setExcludedWeekdays] = useState<number[]>([0]);
  const [excludedRanges, setExcludedRanges] = useState<ExcludedDateRange[]>([]);
  const [rangeStart, setRangeStart] = useState("");
  const [rangeEnd, setRangeEnd] = useState("");
  const [preparingImage, setPreparingImage] = useState(false);
  const cameraInput = useRef<HTMLInputElement>(null);
  const galleryInput = useRef<HTMLInputElement>(null);
  const startDate = useMemo(() => localDateKey(), []);
  const excludedDates = useMemo(() => {
    const dates = new Set<string>();
    excludedRanges.forEach((range) => {
      for (let date = range.start; date <= range.end; date = addDays(date, 1)) dates.add(date);
    });
    return [...dates].sort();
  }, [excludedRanges]);
  const create = useMutation({
    mutationFn: async () => {
      const child = data.data?.children.find((item) => item.id === preferredChildId) ?? data.data?.children[0];
      if (!child) throw new Error("请先创建孩子档案");
      const homework = await api.createHomework({
        childId: child.id,
        subject: form.subject,
        title: form.title.trim(),
        total: Number(form.total),
        unit: form.unit,
        startDate,
        targetDate: form.targetDate,
        excludedWeekdays,
        excludedDates,
      });
      const plan = await api.generatePlan(homework.id);
      return plan;
    },
    onSuccess: () => {
      setCreatedTitle(form.title);
      setCreatedCount(1);
      setCreatedTargetDate(form.targetDate);
      setMode("success");
      setError("");
      void queryClient.invalidateQueries({ queryKey: ["bootstrap"] });
    },
    onError: (cause) => setError(cause instanceof Error ? cause.message : "创建失败"),
  });
  const createBatch = useMutation({
    mutationFn: async () => {
      const child = data.data?.children.find((item) => item.id === preferredChildId) ?? data.data?.children[0];
      if (!child) throw new Error("请先创建孩子档案");
      return api.createHomeworks(ocrDrafts.map((draft) => ({
        childId: child.id,
        subject: draft.subject,
        title: draft.title.trim(),
        total: Number(draft.total),
        unit: draft.unit,
        startDate,
        targetDate: draft.targetDate,
        excludedWeekdays,
        excludedDates,
      })));
    },
    onSuccess: (result) => {
      setCreatedTitle(`${result.count} 项识别作业`);
      setCreatedCount(result.count);
      setCreatedTargetDate(ocrDrafts.reduce((latest, item) => item.targetDate > latest ? item.targetDate : latest, startDate));
      setMode("success");
      setError("");
      void queryClient.invalidateQueries({ queryKey: ["bootstrap"] });
    },
    onError: (cause) => setError(cause instanceof Error ? cause.message : "批量创建失败"),
  });
  const recognize = useMutation({
    mutationFn: (image: string) => api.recognizeHomework(image),
    onSuccess: (result) => {
      setOcrResult(result);
      setBulkDateApplied("");
      if (result.items.length > 1) {
        setOcrDrafts(result.items.map((item, index) => ({
          key: `${index}-${item.section}-${item.title.slice(0, 12)}`,
          section: item.section,
          subject: item.subject,
          title: item.title,
          total: String(item.total),
          unit: item.unit,
          targetDate: item.targetDate && item.targetDate >= startDate ? item.targetDate : form.targetDate,
        })));
      } else {
        setOcrDrafts([]);
        setForm((current) => ({
          ...current,
          title: (result.items[0]?.title ?? result.text).slice(0, 2000),
          subject: result.items[0]?.subject ?? result.suggestions.subject ?? current.subject,
          total: result.items[0]?.total ? String(result.items[0].total) : result.suggestions.total ? String(result.suggestions.total) : current.total,
          unit: result.items[0]?.unit ?? result.suggestions.unit ?? current.unit,
          targetDate: result.items[0]?.targetDate && result.items[0].targetDate >= startDate
            ? result.items[0].targetDate
            : result.suggestions.targetDate && result.suggestions.targetDate >= startDate
              ? result.suggestions.targetDate
              : current.targetDate,
        }));
      }
      setError("");
    },
    onError: (cause) => setError(cause instanceof Error ? cause.message : "图片识别失败，请重试"),
  });

  if (data.isPending) return <LoadingScreen />;
  if (data.isError) return <ErrorScreen message={data.error.message} onRetry={() => void data.refetch()} />;
  const selectedChild = data.data.children.find((item) => item.id === preferredChildId) ?? data.data.children[0];
  const isBatchOcr = Boolean(ocrResult && ocrResult.items.length > 1);

  function selectChild(childId: string) {
    setPreferredChildId(childId);
    savePreferredChildId(childId);
  }

  function submit(event: FormEvent) {
    event.preventDefault();
    setError("");
    create.mutate();
  }

  async function selectImage(event: ChangeEvent<HTMLInputElement>) {
    const file = event.target.files?.[0];
    event.target.value = "";
    if (!file) return;
    setError("");
    setOcrResult(null);
    setOcrDrafts([]);
    setPreparingImage(true);
    try {
      const prepared = await prepareOcrImage(file);
      setOcrImage(prepared);
      recognize.mutate(prepared.dataUrl);
    } catch (cause) {
      setOcrImage(null);
      setError(cause instanceof Error ? cause.message : "图片处理失败，请重试");
    } finally {
      setPreparingImage(false);
    }
  }

  function clearOcrImage() {
    setOcrImage(null);
    setOcrResult(null);
    setOcrDrafts([]);
    setError("");
  }

  function patchDraft(key: string, patch: Partial<OcrDraft>) {
    setOcrDrafts((current) => current.map((draft) => draft.key === key ? { ...draft, ...patch } : draft));
  }

  function removeDraft(key: string) {
    setOcrDrafts((current) => current.filter((draft) => draft.key !== key));
  }

  function applyBulkTargetDate() {
    if (!bulkTargetDate || bulkTargetDate < startDate) {
      setError("统一截止日期不能早于今天");
      return;
    }
    setOcrDrafts((current) => current.map((draft) => ({ ...draft, targetDate: bulkTargetDate })));
    setBulkDateApplied(`已将 ${ocrDrafts.length} 项统一设置为 ${bulkTargetDate} 截止`);
    setError("");
  }

  function toggleExcludedWeekday(day: number) {
    setExcludedWeekdays((current) => current.includes(day) ? current.filter((item) => item !== day) : [...current, day].sort());
  }

  function addExcludedRange() {
    if (!rangeStart || !rangeEnd) return;
    if (rangeEnd < rangeStart) {
      setError("结束日期不能早于开始日期");
      return;
    }
    const dates = new Set(excludedDates);
    for (let date = rangeStart; date <= rangeEnd; date = addDays(date, 1)) dates.add(date);
    if (dates.size > 60) {
      setError("出游或其他事项日期累计最多设置 60 天");
      return;
    }
    const id = `${rangeStart}:${rangeEnd}`;
    setExcludedRanges((current) => current.some((range) => range.id === id) ? current : [...current, { id, start: rangeStart, end: rangeEnd }]);
    setRangeStart("");
    setRangeEnd("");
    setError("");
  }

  function submitBatch(event: FormEvent) {
    event.preventDefault();
    setError("");
    if (!ocrDrafts.length) {
      setError("请至少保留一项作业");
      return;
    }
    createBatch.mutate();
  }

  if (mode === "success") {
    return (
      <section className="screen capture-screen">
        <PageHeader title="确认计划" back />
        <div className="success-hero">
          <span><Check size={29} /></span>
          <div><h2>{createdCount > 1 ? `${createdCount} 项计划已生成` : "计划已生成"}</h2><p>已根据各项截止日期安排每日任务</p></div>
        </div>
        <div className="plan-summary">
          <div><span>作业</span><strong>{createdTitle}</strong></div>
          <div><span>日期</span><strong>{startDate} 至 {createdTargetDate || form.targetDate}</strong></div>
          <div><span>数量</span><strong>{createdCount > 1 ? `${createdCount} 项作业` : `${form.total} ${form.unit}`}</strong></div>
        </div>
        <p className="helper-copy">计划已经保存。你可以在“今天”页面完成任务，在“作业”页面查看全部作业。</p>
        <Link className="primary-button bottom-action" to="/">查看今天任务</Link>
      </section>
    );
  }

  return (
    <section className="screen capture-screen">
      <PageHeader title="录入作业" />
      <div className="capture-methods">
        <button type="button" onClick={() => document.getElementById("title")?.focus()}>
          <ClipboardPaste size={25} /><span><strong>粘贴文字</strong><small>将老师发布的内容粘贴到下方</small></span><ChevronRight size={21} />
        </button>
        <button type="button" onClick={() => cameraInput.current?.click()} disabled={preparingImage || recognize.isPending}>
          <Camera size={25} /><span><strong>拍照识别</strong><small>调用后置摄像头，识别后可人工校对</small></span><ChevronRight size={21} />
        </button>
        <button type="button" onClick={() => galleryInput.current?.click()} disabled={preparingImage || recognize.isPending}>
          <ImagePlus size={25} /><span><strong>上传图片识别</strong><small>从手机相册选择作业通知或纸质作业</small></span><ChevronRight size={21} />
        </button>
        <input ref={cameraInput} className="visually-hidden" type="file" accept="image/*" capture="environment" onChange={selectImage} />
        <input ref={galleryInput} className="visually-hidden" type="file" accept="image/jpeg,image/png,image/webp,image/heic,image/heif" onChange={selectImage} />
      </div>
      <p className="ocr-privacy-note">拍照或上传后，图片将加密发送至百度智能云 OCR，仅用于本次文字识别，应用不保存原图。查看<Link to="/privacy" target="_blank">隐私政策</Link></p>

      {ocrImage ? (
        <section className="ocr-preview" aria-live="polite">
          <img src={ocrImage.dataUrl} alt="待识别的作业图片" />
          <div className="ocr-preview-copy">
            <strong>{preparingImage ? "正在压缩图片…" : recognize.isPending ? "正在识别文字…" : ocrResult ? "识别完成，请校对" : "识别未完成"}</strong>
            <small>{ocrImage.width}×{ocrImage.height} · {(ocrImage.bytes / 1024).toFixed(0)}KB · 识别后立即丢弃</small>
            {ocrResult ? <small>{ocrResult.provider === "mock" ? "本地测试模式" : `百度 OCR${ocrResult.confidence == null ? "" : ` · 置信度 ${Math.round(ocrResult.confidence * 100)}%`}`}</small> : null}
          </div>
          <div className="ocr-preview-actions">
            {!recognize.isPending && !ocrResult ? <button type="button" aria-label="重新识别" onClick={() => recognize.mutate(ocrImage.dataUrl)}><RotateCcw size={18} /></button> : null}
            <button type="button" aria-label="移除图片" onClick={clearOcrImage} disabled={recognize.isPending}><Trash2 size={18} /></button>
          </div>
        </section>
      ) : null}

      {ocrResult ? <p className="ocr-success"><Check size={16} /> {isBatchOcr ? `已按标题和编号拆分为 ${ocrDrafts.length} 项，请逐项校对或移除不需要的项目。` : `已自动填写作业内容${Object.keys(ocrResult.suggestions).length ? "、科目、数量或日期" : ""}，保存前请核对。`}</p> : null}

      {isBatchOcr ? (
        <form className="homework-form batch-homework-form" onSubmit={submitBatch}>
          <label className="field full">
            <span>选择孩子</span>
            <select value={selectedChild?.id ?? ""} onChange={(event) => selectChild(event.target.value)} required>
              {data.data.children.map((child) => <option key={child.id} value={child.id}>{child.nickname} · {child.grade || "未设置年级"}</option>)}
            </select>
          </label>
          <section className="bulk-deadline-tool">
            <div><strong>统一设置截止日期</strong><small>不会限制逐项修改，应用后仍可单独调整</small></div>
            <label className="field"><span>统一截止日期</span><DateInput min={startDate} value={bulkTargetDate} onChange={(event) => { setBulkTargetDate(event.target.value); setBulkDateApplied(""); }} /></label>
            <button className="small-button" type="button" onClick={applyBulkTargetDate}>应用到全部 {ocrDrafts.length} 项</button>
            {bulkDateApplied ? <p role="status"><Check size={15} /> {bulkDateApplied}</p> : null}
          </section>
          <PlanningRules startDate={startDate} excludedWeekdays={excludedWeekdays} excludedRanges={excludedRanges} rangeStart={rangeStart} rangeEnd={rangeEnd} onToggleWeekday={toggleExcludedWeekday} onRangeStart={(date) => { setRangeStart(date); if (!rangeEnd || rangeEnd < date) setRangeEnd(date); }} onRangeEnd={setRangeEnd} onAddRange={addExcludedRange} onRemoveRange={(id) => setExcludedRanges((current) => current.filter((range) => range.id !== id))} />
          <div className="ocr-draft-list">
            {ocrDrafts.map((draft, index) => (
              <article className="ocr-draft-card" key={draft.key}>
                <header><div><strong>第 {index + 1} 项</strong><small>{draft.section}</small></div><button type="button" aria-label={`移除第${index + 1}项`} onClick={() => removeDraft(draft.key)}><Trash2 size={17} /></button></header>
                <label className="field full"><span>作业内容</span><textarea value={draft.title} maxLength={2000} onChange={(event) => patchDraft(draft.key, { title: event.target.value })} required /><small>{draft.title.length}/2000</small></label>
                <div className="form-grid">
                  <label className="field"><span>科目</span><select value={draft.subject} onChange={(event) => patchDraft(draft.key, { subject: event.target.value })}>{SUBJECTS.map((subject) => <option key={subject}>{subject}</option>)}</select></label>
                  <label className="field"><span>单位</span><select value={draft.unit} onChange={(event) => patchDraft(draft.key, { unit: event.target.value })}>{UNITS.map((unit) => <option key={unit}>{unit}</option>)}</select></label>
                </div>
                <div className="form-grid amount-date-grid">
                  <label className="field"><span>总量</span><input inputMode="numeric" type="number" min="1" max="10000" value={draft.total} onChange={(event) => patchDraft(draft.key, { total: event.target.value })} required /></label>
                  <label className="field"><span>截止日期</span><DateInput min={startDate} value={draft.targetDate} onChange={(event) => patchDraft(draft.key, { targetDate: event.target.value })} required /></label>
                </div>
              </article>
            ))}
          </div>
          {error ? <p className="form-error" role="alert">{error}</p> : null}
          <button className="primary-button submit-button" type="submit" disabled={createBatch.isPending || !ocrDrafts.length}><WandSparkles size={21} /> {createBatch.isPending ? "正在创建多项计划…" : `确认并创建 ${ocrDrafts.length} 项作业`}</button>
        </form>
      ) : (
        <form className="homework-form" onSubmit={submit}>
          <label className="field full">
            <span>选择孩子</span>
            <select value={selectedChild?.id ?? ""} onChange={(event) => selectChild(event.target.value)} required>
              {data.data.children.map((child) => <option key={child.id} value={child.id}>{child.nickname} · {child.grade || "未设置年级"}</option>)}
            </select>
          </label>
          <label className="field full">
            <span>作业内容</span>
            <textarea id="title" value={form.title} maxLength={2000} placeholder="例如：阅读《草原》并完成课后练习题" onChange={(event) => setForm((current) => ({ ...current, title: event.target.value }))} required />
            <small>{form.title.length}/2000</small>
          </label>
          <div className="form-grid">
            <label className="field"><span>科目</span><select value={form.subject} onChange={(event) => setForm((c) => ({ ...c, subject: event.target.value }))}>{SUBJECTS.map((subject) => <option key={subject}>{subject}</option>)}</select></label>
            <label className="field"><span>单位</span><select value={form.unit} onChange={(event) => setForm((c) => ({ ...c, unit: event.target.value }))}>{UNITS.map((unit) => <option key={unit}>{unit}</option>)}</select></label>
          </div>
          <div className="form-grid amount-date-grid">
            <label className="field"><span>总量</span><input inputMode="numeric" type="number" min="1" max="10000" value={form.total} placeholder="如 60" onChange={(event) => setForm((c) => ({ ...c, total: event.target.value }))} required /></label>
            <label className="field"><span>截止日期</span><DateInput min={startDate} value={form.targetDate} onChange={(event) => setForm((c) => ({ ...c, targetDate: event.target.value }))} required /></label>
          </div>
          <PlanningRules startDate={startDate} excludedWeekdays={excludedWeekdays} excludedRanges={excludedRanges} rangeStart={rangeStart} rangeEnd={rangeEnd} onToggleWeekday={toggleExcludedWeekday} onRangeStart={(date) => { setRangeStart(date); if (!rangeEnd || rangeEnd < date) setRangeEnd(date); }} onRangeEnd={setRangeEnd} onAddRange={addExcludedRange} onRemoveRange={(id) => setExcludedRanges((current) => current.filter((range) => range.id !== id))} />
          {error ? <p className="form-error" role="alert">{error}</p> : null}
          <button className="primary-button submit-button" type="submit" disabled={create.isPending}><WandSparkles size={21} /> {create.isPending ? "正在生成计划…" : "保存并生成计划"}</button>
        </form>
      )}
    </section>
  );
}
